import {
  Box,
  Flex,
  Text,
  Modal,
  ModalContent,
  ModalOverlay,
  Image,
  Button,
  Input,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import React, { memo, useMemo, useEffect, useState, useCallback } from "react";
import IconX from "../../../assets/images/booking/x-circle.svg";
import iconTrash from "../../../assets/images/projectManagement/trash-2.svg";
import iconPlus from "../../../assets/images/projectManagement/plus.svg";
import {
  InputControl,
  SelectControl,
} from "../../../components/form/formControls";
import { useForm } from "react-hook-form";
import { FormBonus } from "../../../components/form/formBonus";
import { useCategories } from "../../../categorys/calculateServiceFees/detail";
import { lockingParticipantCall } from "../../../api/lockingParticipant";
import { lockingRelativeParticipantCall } from "../../../api/lockingRelativeParticipant";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useRef } from "react";
import toast from "react-hot-toast";

const baseURL = process.env.REACT_APP_BACKEND_API || "http://localhost:3001";

const schema = Yup.object().shape({
  employeeProject: Yup.array()
    .of(
      Yup.object({
        id: Yup.mixed().required(),
        employeeId: Yup.string().required("Vui lòng chọn nhân viên"),
        commissionRate: Yup.string().required("Vui lòng nhập phí dịch vụ"),
      }),
    )
    .test("check-empty", "Vui lòng thêm nhân viên tham gia", (value) => {
      return Array.isArray(value) && value.length > 0;
    })
    .test(
      "check-fields",
      "Vui lòng điền đầy đủ thông tin nhân viên tham gia và tỉ lệ",
      function (value) {
        if (!Array.isArray(value)) return true;

        const hasIncomplete = value.some(
          (item) => !item.employeeId || !item.commissionRate,
        );

        if (hasIncomplete) {
          return this.createError({
            message:
              "Vui lòng điền đầy đủ thông tin nhân viên tham gia và tỉ lệ",
            path: this.path,
          });
        }

        return true;
      },
    ),
  // .test(
  //   "check-total-equal",
  //   "Tổng tỉ lệ các nhân viên tham gia phải bằng 100%",
  //   function (value) {
  //     if (!Array.isArray(value)) return true;

  //     const total = value.reduce((sum, item) => {
  //       const rate = parseFloat(item.commissionRate);
  //       return sum + (isNaN(rate) ? 0 : rate);
  //     }, 0);

  //     // Làm tròn để tránh lỗi do số thập phân
  //     if (Math.round(total * 100) / 100 !== 100) {
  //       return this.createError({
  //         message: "Tổng tỉ lệ các nhân viên tham gia phải bằng 100%",
  //         path: this.path,
  //       });
  //     }

  //     return true;
  //   }
  // ),
});

const FormAddParticipant = memo(
  ({ isOpen, onClose, onSubmit, defaultValues, isEdit }) => {
    const {
      reset,
      register,
      handleSubmit,
      watch,
      setValue,
      setError,
      getValues,
      clearErrors,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(schema),
      defaultValues: defaultValues || {},
    });

    const [errorData, setErrorData] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const [focusStates, setFocusStates] = useState({});

    const [employeeProject, setEmployeeProject] = useState([
      {
        id: Date.now(),
        employeeId: "",
        commissionRate: "",
        relativeCommissionRate: 100,
        collaborators: [
          // { id, employeeId, relativeCommissionRate }
        ],
      },
    ]);

    const [shouldFetch, setShouldFetch] = useState(false);

    useEffect(() => {
      const timer = setTimeout(() => {
        setShouldFetch(true);
      }, 1200);

      return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
      setValue("employeeProject", employeeProject);
    }, [employeeProject, setValue]);

    const { constantData } = useCategories(
      watch("bookerId") || defaultValues?.bookerId,
      shouldFetch,
      //   item?.isCollaborator,
      //   item?.relativeId
    );

    const formatEntries = (entries) =>
      Object.entries(entries || {}).map(([key, value]) => ({
        value: key,
        label: value,
      }));

    const employees = useMemo(() => {
      if (!constantData?.participants) return [];

      return Object.entries(constantData.participants).map(
        ([id, { fullName, code }]) => ({
          value: id,
          label: `${fullName} (${code})`,
        }),
      );
    }, [constantData?.participants]);

    useEffect(() => {
      reset(defaultValues);
      setErrorData(false);

      if (defaultValues?.id) {
        const fetchData = async () => {
          try {
            const [rows, rows2] = await Promise.all([
              lockingRelativeParticipantCall.list(defaultValues.id),
              lockingParticipantCall.list(defaultValues.id),
            ]);

            const finalRow = (rows?.length ? rows : rows2) ?? [];

            const { mainRows, ctvRows } = finalRow.reduce(
              (acc, x) => {
                (x?.isCollaborator ? acc.ctvRows : acc.mainRows).push(x);
                return acc;
              },
              { mainRows: [], ctvRows: [] },
            );

            const mapped = mainRows.map((main) => {
              const mainEmployeeId = String(main?.employeeId || "");

              const collaborators = ctvRows
                .filter((c) => String(c?.relativeId || "") === mainEmployeeId)
                .map((c) => ({
                  id: c?.id || c?._id || `${mainEmployeeId}_${c?.employeeId}`,
                  employeeId: c?.employeeId || "",
                  relativeCommissionRate: Number(
                    c?.relativeCommissionRate ?? 0,
                  ),
                }));

              return {
                id:
                  main?.id ||
                  main?._id ||
                  mainEmployeeId ||
                  Date.now() + Math.random(),
                employeeId: mainEmployeeId,
                commissionRate: String(main?.commissionRate ?? ""),
                relativeCommissionRate: Number(
                  main?.relativeCommissionRate ?? main?.commissionRate ?? 100,
                ),
                collaborators,
              };
            });

            setEmployeeProject(
              mapped.length
                ? mapped
                : [
                    {
                      id: Date.now(),
                      employeeId: "",
                      commissionRate: "",
                      relativeCommissionRate: 100,
                      collaborators: [],
                    },
                  ],
            );
          } catch (error) {
            console.error("Error fetching data:", error);
          }
        };

        fetchData();
      } else {
        setEmployeeProject([
          {
            id: Date.now(),
            employeeId: "",
            commissionRate: "",
            relativeCommissionRate: 100,
            collaborators: [],
          },
        ]);
      }
    }, [isOpen]);

    const addMember = () => {
      setEmployeeProject((prev) => [
        ...prev,
        {
          id: Date.now(),
          employeeId: "",
          commissionRate: "",
          relativeCommissionRate: 100,
          collaborators: [],
        },
      ]);
    };

    const deleteMember = (id) => {
      setEmployeeProject((prev) => prev.filter((m) => m.id !== id));
      clearErrors("employeeProject");
    };

    const updateMemberField = (id, field, value) => {
      setEmployeeProject((prev) =>
        prev.map((m) => {
          if (m.id !== id) return m;

          if (field === "employeeId" && m.employeeId !== value) {
            return { ...m, employeeId: value, collaborators: [] };
          }

          return { ...m, [field]: value };
        }),
      );
    };

    const addCollaborator = (memberId) => {
      setEmployeeProject((prev) =>
        prev.map((m) =>
          m.id === memberId
            ? {
                ...m,
                collaborators: [
                  ...(m.collaborators || []),
                  {
                    id: Date.now(),
                    employeeId: "",
                    relativeCommissionRate: "",
                  },
                ],
              }
            : m,
        ),
      );
    };

    const deleteCollaborator = (memberId, ctvId) => {
      setEmployeeProject((prev) =>
        prev.map((m) =>
          m.id === memberId
            ? {
                ...m,
                collaborators: (m.collaborators || []).filter(
                  (c) => c.id !== ctvId,
                ),
              }
            : m,
        ),
      );
    };

    const updateCollaboratorField = (memberId, ctvId, field, value) => {
      console.log({ memberId, ctvId, field, value });
      setEmployeeProject((prev) =>
        prev.map((m) =>
          m.id === memberId
            ? {
                ...m,
                collaborators: (m.collaborators || []).map((c) =>
                  c.id === ctvId ? { ...c, [field]: value } : c,
                ),
              }
            : m,
        ),
      );
    };

    const handleFocus = (field, index) => {
      if (index !== null) {
        setFocusedIndex(index);
      } else {
        setFocusStates((prev) => ({ ...prev, [field]: true }));
      }
    };

    const handleBlur = (field, index) => {
      if (index !== null) {
        setFocusedIndex(index);
      } else {
        setFocusedIndex(null);
      }
      setFocusStates((prev) => ({ ...prev, [field]: false }));
    };

    const submitHandler = (data) => {
      data.employeeProject = employeeProject; // nested
      onSubmit(data);
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent
          backgroundColor="var(--color-main)"
          borderRadius={"16px"}
          position={"relative"}
          w={"100%"}
          maxW={"625px"}
        >
          <form onSubmit={handleSubmit(submitHandler)}>
            <Box
              // paddingBottom={"80px"}
              h={"86.5vh"}
              w={"100%"}
              overflowY={"scroll"}
              sx={{
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  width: "4px",
                  borderRadius: "50px",
                  background: "var(--color-background)",
                },
                "&::-moz-scrollbar": {
                  width: "4px",
                },
                "&::-moz-scrollbar-thumb": {
                  width: "4px",
                  borderRadius: "50px",
                  background: "var(--color-background)",
                },
              }}
            >
              <Flex
                alignItems={"center"}
                p={"25px 24px 24px 24px"}
                position={"relative"}
                borderBottom={"1px solid var(--border-input-employee)"}
              >
                <Text
                  color={"var(--color-header-employee)"}
                  fontSize={"16px"}
                  fontWeight={"600"}
                >
                  {isEdit
                    ? "Chỉnh sửa nhân viên và CTV tham gia locking"
                    : "Thêm nhân viên và CTV tham gia locking"}
                </Text>
                <Box
                  position={"absolute"}
                  top={"24px"}
                  right={"24px"}
                  cursor={"pointer"}
                  onClick={onClose}
                >
                  <Image src={IconX} />
                </Box>
              </Flex>
              <Box margin={"0 auto"}>
                <Box p={"24px 40px"}>
                  <Box flex={1}>
                    <Box>
                      <Text
                        color={"var(--color-header-employee)"}
                        fontSize={"16px"}
                        fontWeight={"600"}
                      >
                        Thông tin chung
                      </Text>
                      <Box mt={"16px"} borderRadius={"8px"}>
                        <Flex mt={"12px"} gap={"27px"}>
                          {defaultValues?.createdAt && (
                            <Box flex={"1"}>
                              <Text
                                color={"var(--color-label-employee)"}
                                fontSize={"12px"}
                              >
                                Tên đợt locking
                              </Text>
                              <Text
                                color={"var(--color-option-employee)"}
                                fontSize={"14px"}
                                fontWeight={"500"}
                                mt={"8px"}
                              >
                                {defaultValues?.note || "-"}
                              </Text>
                            </Box>
                          )}
                        </Flex>
                        {isEdit && (
                          <Flex mt={"12px"} gap={"27px"}>
                            {defaultValues?.createdAt && (
                              <Box flex={"1"}>
                                <Text
                                  color={"var(--color-label-employee)"}
                                  fontSize={"12px"}
                                >
                                  Ngày tạo locking
                                </Text>
                                <Text
                                  color={"var(--color-option-employee)"}
                                  fontSize={"14px"}
                                  fontWeight={"500"}
                                  mt={"8px"}
                                >
                                  {defaultValues?.createdAt}
                                </Text>
                              </Box>
                            )}
                          </Flex>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Box margin={"24px auto"}>
                    <Flex flexDirection={"column"} gap={"16px"}>
                      <Flex
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <Text
                          fontSize={"16px"}
                          fontWeight={600}
                          lineHeight={"normal"}
                          color="var(--color-black)"
                        >
                          Nhân viên tham gia
                        </Text>
                      </Flex>
                      <Flex
                        mt={"12px"}
                        flexDirection={"column"}
                        gap={"12px"}
                        alignItems={"flex-end"}
                      >
                        <Flex
                          justifyContent={"space-between"}
                          alignItems={"flex-start"}
                          gap={"12px"}
                          w={"100%"}
                          flexDirection={"column"}
                        >
                          {employeeProject.map((m, idx) => (
                            <MemberRow
                              key={m.id}
                              m={m}
                              idx={idx}
                              shouldFetch={shouldFetch}
                              employees={employees}
                              updateMemberField={updateMemberField}
                              deleteMember={deleteMember}
                              addCollaborator={addCollaborator}
                              updateCollaboratorField={updateCollaboratorField}
                              deleteCollaborator={deleteCollaborator}
                            />
                          ))}
                        </Flex>
                        {errors?.employeeProject?.message && (
                          <Text
                            w={"100%"}
                            color={"var(--text-red-employee)"}
                            fontSize={"12px"}
                          >
                            {errors?.employeeProject?.message}
                          </Text>
                        )}
                        <Flex
                          gap={"4px"}
                          alignItems={"end"}
                          p="8px 16px"
                          border={
                            "1px solid var(--color-option-employee-hover)"
                          }
                          borderRadius={"8px"}
                          w={"max-content"}
                          cursor={"pointer"}
                          bg={"var(--color-main)"}
                          onClick={addMember}
                          _hover={{
                            filter: "brightness(90%)",
                          }}
                        >
                          <Image src={iconPlus} />
                          <Text
                            fontSize={"12px"}
                            lineHeight={"15px"}
                            color={"var(--color-option-employee-hover)"}
                            fontWeight={600}
                          >
                            Thêm NV tham gia
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Box>
                </Box>
              </Box>
              <Box
                // position={"absolute"}
                w={"100%"}
                // bottom={"0"}
                bg={"var(--color-main)"}
                borderTop={"0.5px solid var(--bg-line-employee)"}
                borderRadius={"0px 0px 16px 16px"}
                zIndex={3}
              >
                <Flex p={"24px"} gap={"16px"} justifyContent={"flex-end"}>
                  <Button
                    w={"112px"}
                    p={"13px 20px"}
                    fontSize={"15px"}
                    fontWeight={600}
                    color={"var(--color-option-employee-hover)"}
                    borderRadius={"8px"}
                    border={"1px solid var(--color-option-employee-hover)"}
                    bg={"var(--color-main)"}
                    _hover={{
                      filter: "brightness(90%)",
                    }}
                    onClick={onClose}
                  >
                    Hủy
                  </Button>
                  <Button
                    type="submit"
                    w={"112px"}
                    p={"13px 20px"}
                    fontSize={"15px"}
                    fontWeight={600}
                    color={"var(--color-main)"}
                    borderRadius={"8px"}
                    bg={"var(--color-option-employee-hover)"}
                    _hover={{
                      filter: "brightness(90%)",
                    }}
                  >
                    Lưu
                  </Button>
                </Flex>
              </Box>
            </Box>
          </form>
        </ModalContent>
      </Modal>
    );
  },
);

const formatEntries = (entries) =>
  Object.entries(entries || {}).map(([key, value]) => ({
    value: key,
    label: value,
  }));

const MemberRow = memo(function MemberRow({
  m,
  idx,
  shouldFetch,
  employees,
  updateMemberField,
  deleteMember,
  addCollaborator,
  updateCollaboratorField,
  deleteCollaborator,
}) {
  const { constantData: ctv } = useCategories(
    null,
    shouldFetch,
    true,
    m?.employeeId,
  );

  const toOptions = (obj) =>
    Object.entries(obj || {}).map(([id, v]) => {
      if (typeof v === "string") {
        return { value: id, label: v };
      }

      const fullName = v?.fullName || v?.name || "";
      const code = v?.code ? ` (${v.code})` : "";
      return { value: id, label: `${fullName}${code}`.trim() || id };
    });

  const ctvOptions = useMemo(
    () => toOptions(ctv?.participants),
    [ctv?.participants],
  );

  return (
    <Box w="100%">
      {m?.commissionRate ? (
        <Text fontSize="13px" fontWeight={600} mb={2}>
          Tỉ lệ: {m?.commissionRate} %
        </Text>
      ) : (
        <Text fontSize="13px" fontWeight={600} mb={2} color={"red"}>
          Chưa thêm tỉ lệ người bán chính
        </Text>
      )}
      {/* MEMBER */}
      <FormBonus
        zIndex={idx}
        titleRate="Tỉ lệ"
        isIconPlace
        length="100%"
        onChangeName={(e) => updateMemberField(m.id, "employeeId", e)}
        onChangeBonus={(e) =>
          updateMemberField(m.id, "relativeCommissionRate", e)
        }
        deleteData={() => deleteMember(m.id)}
        icon={iconTrash}
        nameMain={m?.relativeCommissionRate || null}
        selectedMain={m.employeeId}
        filteredOptions={employees}
      />

      {/* CTV LIST */}
      <Box
        mt="8px"
        ml="18px"
        pl="12px"
        borderLeft="2px solid var(--border-input-employee)"
      >
        <Flex justifyContent="space-between" alignItems="center" mb="8px">
          <Text fontSize="13px" fontWeight={600}>
            Cộng tác viên (CTV)
          </Text>

          <Button
            size="sm"
            onClick={() => addCollaborator(m.id)}
            isDisabled={!m?.employeeId}
          >
            + Thêm CTV
          </Button>
        </Flex>

        {(m.collaborators || []).map((c) => (
          <Flex key={c.id} gap="10px" alignItems="center" mb="10px">
            <Box flex={1}>
              <SelectControl
                dataOption={ctvOptions}
                selectedMain={c.employeeId}
                onChange={(val) =>
                  updateCollaboratorField(m.id, c.id, "employeeId", val)
                }
                placeholder={
                  m?.employeeId ? "Chọn CTV" : "Chọn NV tham gia trước"
                }
                isDisabled={!m?.employeeId}
              />
            </Box>

            <Box w="140px">
              <Input
                value={c.relativeCommissionRate}
                onChange={(e) =>
                  updateCollaboratorField(
                    m.id,
                    c.id,
                    "relativeCommissionRate",
                    e.target.value,
                  )
                }
                placeholder="% CTV"
                isDisabled={!m?.employeeId}
              />
            </Box>

            <Image
              src={iconTrash}
              cursor="pointer"
              onClick={() => deleteCollaborator(m.id, c.id)}
            />
          </Flex>
        ))}
      </Box>
    </Box>
  );
});

export default FormAddParticipant;
